/**
 * vmx-collection.js
 * Phase 7 — collection sort/filter progressive enhancement.
 *
 * Features:
 *   - Sort select replaces URL param and replaces only the product grid
 *     via Shopify section rendering API (?section_id=vmx-collection-main).
 *   - Filter form submit replaces grid via section rendering API too.
 *   - Mobile filter sidebar toggle.
 *   - Graceful no-JS degradation: form/select submit naturally, page reloads.
 *
 * Security: uses replaceChildren() + cloneNode(true). No unsafe content assignment.
 * Custom element: vmx-collection. Event: vmx:collection:filter-changed.
 */

(function () {
  'use strict';

  var SECTION_ID = 'vmx-collection-main';

  /** Build a URL that swaps or removes a single query param. */
  function buildUrl(base, params) {
    var url = new URL(base, window.location.origin);
    Object.keys(params).forEach(function (k) {
      if (params[k] === null || params[k] === undefined || params[k] === '') {
        url.searchParams.delete(k);
      } else {
        url.searchParams.set(k, params[k]);
      }
    });
    return url;
  }

  /**
   * Parse fetched section HTML using DOMParser (never assigned to .innerHTML
   * on a live node), find the target element by id, and swap it with
   * cloneNode(true) so no scripts execute.
   */
  function parseSectionHtml(html) {
    var parser = new DOMParser();
    return parser.parseFromString(html, 'text/html');
  }

  /**
   * Fetch the same page with ?section_id appended, extract the grid + pagination,
   * and swap them into the DOM using replaceWith / replaceChildren / cloneNode.
   */
  async function replaceGrid(targetUrl) {
    var sectionUrl = new URL(targetUrl.toString());
    sectionUrl.searchParams.set('section_id', SECTION_ID);

    var gridEl = document.getElementById('vmx-product-list');
    var wrapEl = gridEl ? gridEl.closest('.vmx-col-grid-wrap') : null;
    if (!wrapEl) return;

    wrapEl.setAttribute('aria-busy', 'true');
    wrapEl.style.opacity = '0.5';

    try {
      var res = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error('Network error ' + res.status);
      var rawHtml = await res.text();

      var doc = parseSectionHtml(rawHtml);
      var incoming = doc.getElementById('vmx-product-list');
      var incomingPagination = doc.querySelector('.vmx-col-pagination');

      // Grid swap
      if (incoming) {
        var freshGrid = document.getElementById('vmx-product-list');
        if (freshGrid) {
          freshGrid.replaceWith(incoming.cloneNode(true));
        } else {
          wrapEl.prepend(incoming.cloneNode(true));
        }
      }

      // Pagination swap
      var existingPagination = wrapEl.querySelector('.vmx-col-pagination');
      if (incomingPagination) {
        if (existingPagination) {
          existingPagination.replaceWith(incomingPagination.cloneNode(true));
        } else {
          wrapEl.appendChild(incomingPagination.cloneNode(true));
        }
      } else if (existingPagination) {
        existingPagination.remove();
      }

      // Count badge swap
      var incomingCount = doc.querySelector('.vmx-col-controls__count');
      var existingCount = document.querySelector('.vmx-col-controls__count');
      if (incomingCount && existingCount) {
        existingCount.replaceChildren(incomingCount.cloneNode(true));
      }

      history.pushState({}, '', targetUrl.toString());

      document.dispatchEvent(new CustomEvent('vmx:collection:filter-changed', {
        bubbles: true,
        detail: { url: targetUrl.toString() },
      }));

    } catch (_err) {
      // Fallback: full page reload
      window.location.href = targetUrl.toString();
    } finally {
      wrapEl.removeAttribute('aria-busy');
      wrapEl.style.opacity = '';
    }
  }

  /** Sort select — intercept change, rebuild URL, replace grid. */
  function initSort() {
    var select = document.querySelector('[data-vmx-sort-select]');
    if (!select) return;

    var form = select.closest('[data-vmx-sort-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
      });
    }

    select.addEventListener('change', function () {
      var url = buildUrl(window.location.href, { sort_by: select.value, page: null });
      replaceGrid(url);
    });
  }

  /** Filter form — intercept submit, rebuild URL from FormData, replace grid. */
  function initFilters() {
    var form = document.getElementById('vmx-filter-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var data = new FormData(form);
      var current = new URL(window.location.href);
      var sortBy = current.searchParams.get('sort_by');

      var next = new URL(current.pathname, window.location.origin);
      if (sortBy) next.searchParams.set('sort_by', sortBy);

      for (var pair of data.entries()) {
        if (pair[1] !== '') next.searchParams.append(pair[0], pair[1]);
      }
      next.searchParams.delete('page');

      replaceGrid(next);
    });

    // Checkboxes auto-submit on change
    var checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    });
  }

  /** Mobile filter sidebar toggle. */
  function initFilterToggle() {
    var btn = document.querySelector('[data-vmx-filter-toggle]');
    if (!btn) return;
    var sidebar = document.getElementById('vmx-filters');
    if (!sidebar) return;

    btn.addEventListener('click', function () {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      sidebar.classList.toggle('is-open', !expanded);
    });
  }

  /** Handle browser back / forward. */
  function initPopState() {
    window.addEventListener('popstate', function () {
      replaceGrid(new URL(window.location.href));
    });
  }

  function init() {
    initSort();
    initFilters();
    initFilterToggle();
    initPopState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
